import React, { useEffect, useState } from "react";
import styles from "./fba-listings-search.module.css";
import TextInput from "@/components/forms/TextInput/TextInput";
import Select from "@/components/forms/Select/Select";
import IconSearch from "@/components/icons/IconSearch";
import { useFocus } from "@/hooks/useFocus";
import { Product, useInventoryContext } from "@/contexts/InventoryContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { getCatalogItems } from "@/api/products";
import Button from "@/components/forms/Button/Button";
import FbaListingsSearchItem from "../FbaListingsSearchItem/FbaListingsSearchItem";
import FbaAddListingDrawer from "../FbaAddListingDrawer";
import ProductListItemSkeleton from "@/components/ProductListItemSkeleton/ProductListItemSkeleton";
import Flex from "@/components/Flex/Flex";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Pagination from "@/components/Pagination/Pagination";

export enum FbaListingsSearchMode {
  allProducts = "All products",
  inventoryProducts = "Active products",
  amazonCatalog = "Amazon catalog"
}

const cachedCatalogSearchResults: { [key: string]: Product[] } = {};

let searchTimeoutId;

type FbaListingsSearchProps = {};

const FbaListingsSearch = ({ }: FbaListingsSearchProps) => {
  const { isFetchingInventory, inventory, getSalesRanksForASINs } = useInventoryContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const [searchMode, setSearchMode] = useState(FbaListingsSearchMode.allProducts);
  const [searchInputRef, setSearchInputFocus] = useFocus<HTMLInputElement>();
  const [searchInputText, setSearchInputText] = useState<string>("");
  const [defaultSku, setDefaultSKu] = useState<string>("");
  const [isLoadingSearchResults, setIsLoadingSearchResults] = useState<boolean>(false);
  const [inventoryResults, setInventoryResults] = useState<Product[]>([]);
  const [catalogResults, setCatalogResults] = useState<Product[]>([]);
  const [inventoryView, setInventoryView] = useState<Product[]>([]);
  const [catalogView, setCatalogView] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [addedProduct, setAddedProduct] = useState<Product>();
  const [sellerItemIndex, setSellerItemIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<"form" | "details" | "offers">("form");

  const setAddedItemData = (product: Partial<Product>, sellerItemIndex: number, defaultSkuValue: string) => {
    setSellerItemIndex(sellerItemIndex);
    setAddedProduct({ ...product } as Product);
    setDefaultSKu(defaultSkuValue);
  };

  const finalizeSearchResults = async (inventorySearchResults: Product[], catalogSearchResults: Product[]) => {
    const pageSize = 10; // we hardcode this because 10 is actually a constraint relating to rate-limits.
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = (currentPage - 1) * pageSize + pageSize;
    let inventoryView = [];

    if (inventorySearchResults.length > 0) {
      setIsLoadingSearchResults(true); // we have to set this here as well as in one calling context, because they are sometimes used indepdendently

      // we need to make a separate API call to get the sales rank of inventory items,
      // since this is a fluctuating value which we don't store in the db.

      // unfortunately, this uses the same SPAPI endpoint that catalog search uses, and is limited to
      // only 10 ASINs per request. this means we should probably force the page size for search results to
      // be 10. The catalog search has a rate limit of 2/s, which we dance the line of crossing with this
      // if the user is searching rapidly (we debounce at 350ms, so we're essentially relying on 650ms of latency from
      // other sources! we need to get to 1s, because again we make 2 requests per search, assuming we need to get sales
      // ranks for inventory items).

      // when we have to make this request, we're usually taking ~800ms to do it and render the page on my machine.
      // when the results are cached, the time goes to 0ms in the js, and it's just browser layout latency.

      // figure out the slice of the inventory results we need to make a request for.
      // dependent on the currently selected page, and whether or not we have the cached results
      // for these ASINs or not. @NOTE we currently make a request for up to 10 ASINs even if 
      // there's only a single ASIN in the slice that isn't cached. This is probably fine...
      inventoryView = inventorySearchResults.slice(startIndex, Math.min(endIndex, inventorySearchResults.length));
      // get the sales ranks for the search results:
      try {
        const salesRanks = await getSalesRanksForASINs(inventoryView.map(i => i.asin));
        inventoryView.forEach(product => {
          const salesRank = salesRanks.find(salesRank => salesRank.asin === product.asin);
          if (salesRank) {
            product.salesRank = salesRank.salesRank;
            product.category = salesRank.category;
            product.categoryLink = salesRank.link;
          }
        });
      } catch (e: any) {
        if (handleErrorResponseGenerically(e)) {
          return;
        }
      }
    }

    setInventoryView(inventoryView);
    if (inventoryView.length < 10) {
      const range = 10 - inventoryView.length;
      const catalogStart = Math.max(0, startIndex - inventorySearchResults.length);
      setCatalogView(catalogSearchResults.slice(catalogStart, catalogStart + range));
    } else {
      // important to clear catalog items when paginating
      setCatalogView([]);
    }

    // important to set these after the sales rank api request, otherwise we can get weird situations
    // where the pagination appears ~0.5s before the search results does.
    setInventoryResults(inventorySearchResults);
    setCatalogResults(catalogSearchResults);

    setIsLoadingSearchResults(false);
  };

  const debouncedSearch = async (searchText: string, searchMode: FbaListingsSearchMode) => {
    // sometimes during transition page in a state where it shows both 
    // no product matched message
    // as well as search results
    // set isLoadingSearchResults first to true fixes it
    setIsLoadingSearchResults(true);
    clearTimeout(searchTimeoutId);

    let searchTerm = searchText.trim();
    setCurrentPage(1);

    if (searchTerm.length === 0) {
      setIsLoadingSearchResults(false);
      // when using backspace to remove search terms it shows no results but still
      // keeps the previous pagination. we need to clean up the results
      await finalizeSearchResults([], []);
      return;
    }

    const originalSearchTerm = searchTerm;
    searchTerm = searchTerm.toLowerCase();

    const catalogSearch = async (searchTerm: string, inventoryResults: Product[]): Promise<Product[]> => {
      const cachedResults = cachedCatalogSearchResults[searchTerm];
      if (cachedResults) {
        // filter duplicate ASINs out (inventory results may already have this ASIN)
        const filteredItems = cachedResults.filter(item => !inventoryResults.find(inventoryItem => item.asin === inventoryItem.asin));
        return filteredItems;
      }

      // we want to skip searching the catalog because our input will be rejected by the API anyway.
      // exactly one or more commas and spaces will be rejected. (any number of spaces will be too, but
      // we trim the input...)
      if (/^[, ]+$/.test(searchTerm)) {
        return [];
      }

      try {
        const [catalogResults] = await getCatalogItems(searchTerm);
        cachedCatalogSearchResults[searchTerm] = catalogResults;
        // filter duplicate ASINs out (inventory results may already have this ASIN)
        const filteredItems = catalogResults.filter(item => !inventoryResults.find(inventoryItem => item.asin === inventoryItem.asin));
        return filteredItems;

      } catch (errors: any) {
        if (handleErrorResponseGenerically(errors)) {
        }
        return [];
      }
    };

    searchTimeoutId = setTimeout(async () => {
      setCatalogResults([]);
      setInventoryResults([]);
      if (searchMode === FbaListingsSearchMode.allProducts || searchMode === FbaListingsSearchMode.inventoryProducts) {
        const inventoryResults: Product[] = [];
        for (let i = 0; i < inventory.length; i++) {
          const inventoryItem: Product = inventory[i];
          const searchTermValues = searchTerm.split(" "); //Split on spaces - potential future upgrade split multiple characters.
          const foundItem = searchTermValues.filter(term => inventoryItem.searchString.includes(term)); //Filter out terms not found in search string.
          if (foundItem.length === searchTermValues.length) inventoryResults.push(inventoryItem); //Push inv item if all terms were present in search string.
        }

        let catalogResults = [];
        if (searchMode === FbaListingsSearchMode.allProducts) catalogResults = await catalogSearch(originalSearchTerm, inventoryResults);
        await finalizeSearchResults(inventoryResults, catalogResults);
      } else {
        const catalogResults = await catalogSearch(originalSearchTerm, []);
        await finalizeSearchResults([], catalogResults);
      }
    }, 350);
  };

  useEffect(() => {
    finalizeSearchResults(inventoryResults, catalogResults);
  }, [currentPage]);

  if (isFetchingInventory) {
    return (
      <div className={styles.listingSearchContainer}>
        <Flex align="center" gap={4}>
          <LoadingSpinner /> Loading your inventory...
        </Flex>
      </div>
    );
  }

  return (
    <>
      <div className={styles.listingSearchContainer}>
        <Flex gap={4}>
          <TextInput
            ref={searchInputRef}
            placeholder="Search by product name, SKU, FNSKU, ASIN, UPC, ISBN, EAN, or GTIN"
            prefix={<Flex align="center" style={{ marginRight: "1ch" }}><IconSearch /></Flex>}
            value={searchInputText}
            onChange={v => {
              setSearchInputText(v);
              debouncedSearch(v, searchMode);
            }}
            maxLength={300}
            inputAutofocus
          />

          <Select
            anchor='right'
            options={[FbaListingsSearchMode.allProducts, FbaListingsSearchMode.inventoryProducts, FbaListingsSearchMode.amazonCatalog]}
            selectionRender={(option) => {
              switch (option) {
                case FbaListingsSearchMode.allProducts:
                  return "All products";

                case FbaListingsSearchMode.inventoryProducts:
                  return "Inventory products";

                case FbaListingsSearchMode.amazonCatalog:
                  return "Catalog products";
              }
            }}
            optionRender={(option) => {
              switch (option) {
                case FbaListingsSearchMode.allProducts:
                  return "All products";

                case FbaListingsSearchMode.inventoryProducts:
                  return (
                    <Flex direction="column">
                      <span>
                        Active products
                      </span>
                      <span className="weak">
                        Products currently in your inventory
                      </span>
                    </Flex>
                  );

                case FbaListingsSearchMode.amazonCatalog:
                  return (
                    <Flex direction="column">
                      <span>
                        Amazon catalog
                      </span>
                      <span className="weak">
                        Products from the entire Amazon catalog
                      </span>
                    </Flex>
                  );
              }
            }}
            value={searchMode}
            onChange={(option) => {
              setSearchMode(option);
              debouncedSearch(searchInputText, option);
            }}
          />
        </Flex>

        <div className={styles.productListContainer}>
          {searchInputText === "" && (
            <Flex
              direction="column"
              className={styles.emptyBox}
            >
              <img src="/images/search.svg" height="100" />
              <p>{`Search by typing a search query above.`}</p>
            </Flex>
          )}

          {!isLoadingSearchResults && catalogResults.length === 0 && inventoryResults.length === 0 && searchInputText !== "" &&
            <Flex
              direction="column"
              className={styles.emptyBox}
            >
              <span className="strong">
                {`No product found matching "${searchInputText}"`}
              </span>
              <img src="/images/search.svg" height="100" />
              <p>{`Try adjusting your filters to find the products you're looking for`}</p>
              <Button onClick={() => { setSearchInputText(""); setSearchInputFocus(); }} type="secondary">
                Clear search
              </Button>
            </Flex>
          }

          {isLoadingSearchResults && (
            <>
              <ProductListItemSkeleton />
              <ProductListItemSkeleton />
              <ProductListItemSkeleton />
            </>
          )}

          {!isLoadingSearchResults && searchInputText !== "" && inventoryView.map((item, index) => {
            return (
              <FbaListingsSearchItem
                key={index}
                product={item}
                onAddListing={setAddedItemData}
                setActiveTab={setActiveTab}
              />
            );
          })}

          {!isLoadingSearchResults && searchInputText !== "" && catalogView.map((product, index) => (
            <FbaListingsSearchItem
              key={index}
              product={product}
              onAddListing={setAddedItemData}
              setActiveTab={setActiveTab}
            />
          ))}
        </div>

        {((inventoryResults.length !== 0 || catalogResults.length !== 0) && ((inventoryResults.length + catalogResults.length) > 10)) && (
          <Pagination
            sticky
            showPageSizeOptions={false}
            total={inventoryResults.length + catalogResults.length}
            pageSize={10}
            onChangePageSize={() => { }}
            page={currentPage}
            onChangePage={(e) => setCurrentPage(e)}
          />
        )}
      </div>

      {addedProduct && (
        <FbaAddListingDrawer
          defaultSku={defaultSku}
          product={addedProduct}
          sellerDataIndex={sellerItemIndex}
          onClose={() => {
            setSearchInputFocus();
            setAddedProduct(undefined);
          }}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
    </>
  );
};

export default FbaListingsSearch;